/**
 * What to tell someone whose sign-up was refused.
 *
 * `POST /user/signup/email` rejects in two shapes. Per-field validation arrives
 * as `data.data.{email,password}` arrays, but a duplicate address answers with
 * `data: []` and the whole message in `title` — "Email already exists. Please
 * login." Reading only the field arrays therefore turned the one refusal a
 * visitor can actually act on into a generic failure.
 */
interface ISignupErrorResponse {
    response?: {
        data?: {
            title?: string;
            data?: { email?: string[]; password?: string[] } | unknown[];
        };
    };
}

const fieldErrors = (error: ISignupErrorResponse | null | undefined) => {
    const data = error?.response?.data?.data;
    // The duplicate-email case sends an array here, which has no field keys.
    return Array.isArray(data) || !data ? undefined : data;
};

export const signupEmailError = (error: ISignupErrorResponse | null | undefined) =>
    fieldErrors(error)?.email?.[0];

export const signupPasswordError = (error: ISignupErrorResponse | null | undefined) =>
    fieldErrors(error)?.password?.[0];

export const signupErrorMessage = (error: ISignupErrorResponse | null | undefined): string =>
    signupEmailError(error) ||
    signupPasswordError(error) ||
    error?.response?.data?.title ||
    "An error occurred during signup.";

/**
 * Whether the refusal is about the address, so the flow can send the visitor
 * back to the email step rather than leaving them on the password one with a
 * message about a field they cannot see.
 */
export const isSignupEmailError = (error: ISignupErrorResponse | null | undefined): boolean =>
    Boolean(signupEmailError(error)) ||
    Boolean(error?.response?.data?.title?.toLowerCase().includes("email"));
